/**
 * Copyright Zendesk, Inc.
 *
 * Use of this source code is governed under the Apache License, Version 2.0
 * found at http://www.apache.org/licenses/LICENSE-2.0.
 */

import React__default, { useContext, useRef, createElement, Children, useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Downshift from 'downshift';
export { resetIdCounter } from 'downshift';
import { Manager, Reference, Popper } from 'react-popper';
import { withTheme, isRtl, DEFAULT_THEME, getColor, retrieveComponentStyles, getLineHeight, arrowStyles, menuStyles } from '@zendeskgarden/react-theming';
import { composeEventHandlers, KEY_CODES, useCombinedRefs } from '@zendeskgarden/container-utilities';
import styled, { css } from 'styled-components';
import { Input, FauxInput, Field as Field$1, Hint as Hint$1, Label as Label$1, Message as Message$1 } from '@zendeskgarden/react-forms';
import { math, stripUnit } from 'polished';
import { useSelection } from '@zendeskgarden/container-selection';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (!(Symbol.iterator in Object(arr) || Object.prototype.toString.call(arr) === "[object Arguments]")) {
    return;
  }

  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

var DropdownContext = React__default.createContext(undefined);
var useDropdownContext = function useDropdownContext() {
  var dropdownContext = useContext(DropdownContext);
  if (!dropdownContext) {
    throw new Error('This component must be rendered within a `Dropdown` component.');
  }
  return dropdownContext;
};

var REMOVE_ITEM_STATE_TYPE = 'REMOVE_ITEM';
var Dropdown = function Dropdown(props) {
  var children = props.children,
      isOpen = props.isOpen,
      selectedItem = props.selectedItem,
      selectedItems = props.selectedItems,
      highlightedIndex = props.highlightedIndex,
      inputValue = props.inputValue,
      onSelect = props.onSelect,
      _onStateChange = props.onStateChange,
      onInputValueChange = props.onInputValueChange,
      downshiftProps = props.downshiftProps;
  var itemIndexRef = useRef(0);
  var previousItemRef = useRef(undefined);
  var previousIndexRef = useRef(undefined);
  var nextItemsHashRef = useRef({});
  var containsMultiselectRef = useRef(false);
  var popperReferenceElementRef = useRef(null);
  var customGetInputProps = function customGetInputProps(_ref, downshift, rtl) {
    var onKeyDown = _ref.onKeyDown,
        other = _objectWithoutProperties(_ref, ["onKeyDown"]);
    return _objectSpread2({
      onKeyDown: composeEventHandlers(onKeyDown, function (e) {
        var PREVIOUS_KEY = rtl ? KEY_CODES.RIGHT : KEY_CODES.LEFT;
        var NEXT_KEY = rtl ? KEY_CODES.LEFT : KEY_CODES.RIGHT;
        if (downshift.isOpen) {
          if (e.keyCode === PREVIOUS_KEY && previousIndexRef.current !== null && previousIndexRef.current !== undefined && !downshift.inputValue) {
            e.preventDefault();
            e.stopPropagation();
            downshift.selectItemAtIndex(previousIndexRef.current);
          }
          if (e.keyCode === NEXT_KEY) {
            var nextItemIndexes = Object.values(nextItemsHashRef.current);
            if (nextItemIndexes.includes(downshift.highlightedIndex)) {
              e.preventDefault();
              e.stopPropagation();
              downshift.selectItemAtIndex(downshift.highlightedIndex);
            }
          }
        } else if ((e.keyCode === KEY_CODES.ENTER || e.keyCode === KEY_CODES.SPACE) && !downshift.isOpen) {
          e.preventDefault();
          e.stopPropagation();
          downshift.openMenu();
        }
      })
    }, other);
  };
  var transformDownshift = function transformDownshift(_ref2) {
    var _getInputProps = _ref2.getInputProps,
        _getToggleButtonProps = _ref2.getToggleButtonProps,
        downshift = _objectWithoutProperties(_ref2, ["getInputProps", "getToggleButtonProps"]);
    return _objectSpread2({
      getInputProps: function getInputProps(p) {
        return _getInputProps(customGetInputProps(p, downshift, isRtl(props)));
      },
      getToggleButtonProps: function getToggleButtonProps(p) {
        return _getToggleButtonProps(_objectSpread2({
          'aria-label': undefined
        }, p));
      }
    }, downshift);
  };
  return React__default.createElement(Manager, null, React__default.createElement(Downshift, _extends({
    suppressRefError: true
    ,
    isOpen: isOpen,
    highlightedIndex: highlightedIndex,
    selectedItem: selectedItem || null
    ,
    inputValue: inputValue,
    onInputValueChange: onInputValueChange,
    onStateChange: function onStateChange(changes, stateAndHelpers) {
      if (Object.prototype.hasOwnProperty.call(changes, 'selectedItem') && changes.selectedItem !== null) {
        if (selectedItems) {
          var itemToString = stateAndHelpers.itemToString;
          var existingItemIndex = selectedItems.findIndex(function (item) {
            return itemToString(item) === itemToString(changes.selectedItem);
          });
          var updatedSelectedItems = Array.from(selectedItems);
          if (existingItemIndex === -1) {
            updatedSelectedItems.splice(updatedSelectedItems.length, 0, changes.selectedItem);
          } else {
            updatedSelectedItems.splice(existingItemIndex, 1);
          }
          changes.selectedItems = updatedSelectedItems;
          delete changes.selectedItem;
          onSelect && onSelect(updatedSelectedItems, stateAndHelpers);
        } else {
          onSelect && onSelect(changes.selectedItem, stateAndHelpers);
        }
        stateAndHelpers.setState({
          inputValue: ''
        });
      }
      _onStateChange && _onStateChange(changes, stateAndHelpers);
    },
    stateReducer: function stateReducer(_state, changes) {
      switch (changes.type) {
        case Downshift.stateChangeTypes.controlledPropUpdatedSelectedItem:
        case Downshift.stateChangeTypes.mouseUp:
        case Downshift.stateChangeTypes.keyDownSpaceButton:
        case Downshift.stateChangeTypes.blurButton:
          return _objectSpread2({}, changes, {
            inputValue: ''
          });
        case Downshift.stateChangeTypes.keyDownEnter:
        case Downshift.stateChangeTypes.clickItem:
        case REMOVE_ITEM_STATE_TYPE:
          return _objectSpread2({}, changes, {
            isOpen: false
          });
        default:
          return changes;
      }
    }
  }, downshiftProps), function (downshift) {
    return React__default.createElement(DropdownContext.Provider, {
      value: {
        itemIndexRef: itemIndexRef,
        previousItemRef: previousItemRef,
        previousIndexRef: previousIndexRef,
        nextItemsHashRef: nextItemsHashRef,
        popperReferenceElementRef: popperReferenceElementRef,
        selectedItems: selectedItems,
        downshift: transformDownshift(downshift),
        containsMultiselectRef: containsMultiselectRef
      }
    }, children);
  }));
};
Dropdown.propTypes = {
  isOpen: PropTypes.bool,
  selectedItem: PropTypes.any,
  selectedItems: PropTypes.arrayOf(PropTypes.any),
  highlightedIndex: PropTypes.number,
  inputValue: PropTypes.string,
  onSelect: PropTypes.func,
  onStateChange: PropTypes.func,
  downshiftProps: PropTypes.object
};
var Dropdown$1 = withTheme(Dropdown);

function _templateObject() {
  var data = _taggedTemplateLiteral(["\n  ", "\n"]);
  _templateObject = function _templateObject() {
    return data;
  };
  return data;
}
var hiddenStyling = css(["position:absolute;border:0;clip:rect(1px,1px,1px,1px);padding:0;width:1px;height:1px;overflow:hidden;white-space:nowrap;"]);
var StyledInput = styled(Input).attrs({
  isBare: true
})(_templateObject(), function (props) {
  return props.isHidden && hiddenStyling;
});
StyledInput.defaultProps = {
  theme: DEFAULT_THEME
};

function _extends$1() { _extends$1 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1.apply(this, arguments); }

var _ref =
/*#__PURE__*/
createElement("path", {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  d: "M4 6.5l3.6 3.6c.2.2.5.2.7 0L12 6.5"
});

function SvgChevronDownStroke(props) {
  return createElement("svg", _extends$1({
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    focusable: "false",
    role: "presentation"
  }, props), _ref);
}

function _extends$2() { _extends$2 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2.apply(this, arguments); }

var _ref$1 =
/*#__PURE__*/
createElement("path", {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  d: "M3 4.5l2.6 2.6c.2.2.5.2.7 0L9 4.5"
});

function SvgChevronDownStroke$1(props) {
  return createElement("svg", _extends$2({
    width: 12,
    height: 12,
    viewBox: "0 0 12 12",
    focusable: "false",
    role: "presentation"
  }, props), _ref$1);
}

function _templateObject3() {
  var data = _taggedTemplateLiteral(["\n  overflow: hidden;\n  text-overflow: ellipsis;\n  white-space: nowrap;\n"]);
  _templateObject3 = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2() {
  var data = _taggedTemplateLiteral(["\n  position: relative;\n  cursor: ", ";\n  appearance: none;\n  /* stylelint-disable-next-line property-no-unknown */\n  padding-", ": ", ";\n  text-align: ", ";\n\n  ", ";\n\n  ", " {\n    transform: ", ";\n    color: ", ";\n  }\n\n  :hover {\n    ", " {\n      color: ", ";\n    }\n  }\n\n  ", ";\n"]);
  _templateObject2 = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$1() {
  var data = _taggedTemplateLiteral(["\n  display: flex;\n  position: absolute;\n  top: 0;\n  /* stylelint-disable-next-line property-no-unknown */\n  ", ": 0;\n  align-items: center;\n  justify-content: center;\n  /* prettier-ignore */\n  transition: color 0.25s ease-in-out,\n    transform 0.25s ease-in-out,\n    border-color 0.25s ease-in-out,\n    box-shadow 0.1s ease-in-out;\n  width: ", ";\n  height: ", ";\n  color: ", ";\n"]);
  _templateObject$1 = function _templateObject() {
    return data;
  };
  return data;
}
var COMPONENT_ID = 'dropdowns.select';
var isInvalid = function isInvalid(validation) {
  return validation === 'warning' || validation === 'error';
};
var getIconSize = function getIconSize(props) {
  if (props.isCompact) {
    return "".concat(props.theme.space.base * 8, "px");
  }
  return "".concat(props.theme.space.base * 10, "px");
};
var StyledSelectIcon = styled.div(_templateObject$1(), function (props) {
  return props.theme.rtl ? 'left' : 'right';
}, function (props) {
  return getIconSize(props);
}, function (props) {
  return getIconSize(props);
}, function (props) {
  return getColor('neutralHue', 600, props.theme);
});
StyledSelectIcon.defaultProps = {
  theme: DEFAULT_THEME
};
var sizeStyles = function sizeStyles(props) {
  var tagMargin = "".concat(props.theme.space.base, "px");
  var tagBorderWidth = math("".concat(props.theme.borderWidths.sm, " * 2"));
  var tagMinimumWidth = "".concat(props.theme.space.base * 22.5, "px");
  var selectPadding = "".concat(props.theme.space.base * 12, "px");
  if (props.isCompact) {
    tagMinimumWidth = "".concat(props.theme.space.base * 13.5, "px");
    selectPadding = "".concat(props.theme.space.base * 10, "px");
  }
  return css(["min-width:", ";"], math("".concat(tagMinimumWidth, " + ").concat(selectPadding, " + ").concat(tagMargin, " + ").concat(tagBorderWidth)));
};
var StyledSelect = styled(FauxInput).attrs(function (props) {
  return {
    'data-garden-id': COMPONENT_ID,
    'data-garden-version': '8.4.0',
    'aria-invalid': isInvalid(props.validation)
  };
})(_templateObject2(), function (props) {
  return props.disabled ? 'default' : 'pointer';
}, function (props) {
  return props.theme.rtl ? 'left' : 'right';
}, function (props) {
  return getIconSize(props);
}, function (props) {
  return props.theme.rtl && 'right';
}, function (props) {
  return sizeStyles(props);
}, StyledSelectIcon, function (props) {
  if (!props.isOpen) {
    return undefined;
  }
  if (props.theme.rtl) {
    return 'rotate(-180deg) translateY(-1px)';
  }
  return 'rotate(180deg) translateY(-1px)';
}, function (props) {
  if (props.disabled) {
    return getColor('neutralHue', 400, props.theme);
  }
  if (props.isHovered) {
    return getColor('neutralHue', 700, props.theme);
  }
  return getColor('neutralHue', 600, props.theme);
}, StyledSelectIcon, function (props) {
  return !props.disabled && getColor('neutralHue', 700, props.theme);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID, props);
});
StyledSelect.defaultProps = {
  theme: DEFAULT_THEME
};
var StyledOverflowWrapper = styled.div(_templateObject3());
StyledOverflowWrapper.defaultProps = {
  theme: DEFAULT_THEME
};

var SelectWrapper = React__default.forwardRef(function (_ref, ref) {
  var children = _ref.children,
      props = _objectWithoutProperties(_ref, ["children"]);
  return React__default.createElement(StyledSelect, _extends({
    ref: ref
  }, props), children, !props.isBare && React__default.createElement(StyledSelectIcon, {
    isCompact: props.isCompact
  }, props.isCompact ? React__default.createElement(SvgChevronDownStroke$1, null) : React__default.createElement(SvgChevronDownStroke, null)));
});
SelectWrapper.displayName = 'SelectWrapper';

function _templateObject4() {
  var data = _taggedTemplateLiteral(["\n  display: inline-block;\n  cursor: ", ";\n  overflow: hidden;\n  user-select: none;\n  text-overflow: ellipsis;\n  line-height: ", ";\n  white-space: nowrap;\n  color: ", ";\n\n  :hover {\n    text-decoration: ", ";\n  }\n"]);
  _templateObject4 = function _templateObject4() {
    return data;
  };
  return data;
}
function _templateObject3$1() {
  var data = _taggedTemplateLiteral(["\n  flex-basis: ", "px;\n  flex-grow: 1;\n  min-height: 0;\n\n  ", ";\n"]);
  _templateObject3$1 = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$1() {
  var data = _taggedTemplateLiteral(["\n  display: inline-flex;\n  align-items: center;\n  margin: ", "px;\n  max-width: 100%;\n"]);
  _templateObject2$1 = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$2() {
  var data = _taggedTemplateLiteral(["\n  display: inline-flex;\n  flex-wrap: wrap;\n\n  ", ";\n"]);
  _templateObject$2 = function _templateObject() {
    return data;
  };
  return data;
}
var sizeStyles$1 = function sizeStyles(props) {
  var tagHeight = props.isCompact ? 20 : 32;
  var inputHeight = props.isCompact ? 32 : 40;
  var tagMargin = props.theme.space.base / 2;
  var inputBorder = props.isBare ? 0 : stripUnit(props.theme.borderWidths.sm);
  var verticalPadding = "".concat((inputHeight - tagHeight - inputBorder * 2 - tagMargin * 2) / 2, "px");
  var horizontalPadding = "".concat(props.theme.space.base * 2, "px");
  return css(["padding-top:", ";padding-bottom:", ";padding-", ":", ";"], verticalPadding, verticalPadding, props.theme.rtl ? 'right' : 'left', horizontalPadding);
};
var StyledMultiSelect = styled(SelectWrapper)(_templateObject$2(), function (props) {
  return sizeStyles$1(props);
});
StyledMultiSelect.defaultProps = {
  theme: DEFAULT_THEME
};
var StyledItemWrapper = styled.div(_templateObject2$1(), function (props) {
  return props.theme.space.base / 2;
});
StyledItemWrapper.defaultProps = {
  theme: DEFAULT_THEME
};
var visibleStyling = function visibleStyling(props) {
  var margin = props.isVisible ? "".concat(props.theme.space.base / 2, "px") : 0;
  var minWidth = props.isVisible ? "".concat(props.theme.space.base * 15, "px") : 0;
  var height = '0';
  if (props.isVisible) {
    height = "".concat(props.theme.space.base * (props.isCompact ? 5 : 8), "px");
  }
  return css(["opacity:", ";margin:", ";width:", ";min-width:", ";height:", ";"], !props.isVisible && 0, margin, !props.isVisible && 0, minWidth, height);
};
var StyledMultiselectInput = styled(StyledInput)(_templateObject3$1(), function (props) {
  return props.theme.space.base * 15;
}, function (props) {
  return visibleStyling(props);
});
StyledMultiselectInput.defaultProps = {
  theme: DEFAULT_THEME
};
var StyledMoreAnchor = styled.div(_templateObject4(), function (props) {
  return props.isDisabled ? 'default' : 'pointer';
}, function (props) {
  return props.isCompact ? '1em' : getLineHeight(props.theme.space.base * 5, props.theme.fontSizes.md);
}, function (props) {
  return props.isDisabled ? getColor('neutralHue', 400, props.theme) : getColor('primaryHue', 600, props.theme);
}, function (props) {
  return !props.isDisabled && 'underline';
});
StyledMoreAnchor.defaultProps = {
  theme: DEFAULT_THEME
};

function getPopperPlacement(gardenPlacement) {
  switch (gardenPlacement) {
    case 'end':
      return 'right';
    case 'end-top':
      return 'right-start';
    case 'end-bottom':
      return 'right-end';
    case 'start':
      return 'left';
    case 'start-top':
      return 'left-start';
    case 'start-bottom':
      return 'left-end';
    default:
      return gardenPlacement;
  }
}
function getRtlPopperPlacement(gardenPlacement) {
  var popperPlacement = getPopperPlacement(gardenPlacement);
  switch (popperPlacement) {
    case 'left':
      return 'right';
    case 'left-start':
      return 'right-start';
    case 'left-end':
      return 'right-end';
    case 'top-start':
      return 'top-end';
    case 'top-end':
      return 'top-start';
    case 'right':
      return 'left';
    case 'right-start':
      return 'left-start';
    case 'right-end':
      return 'left-end';
    case 'bottom-start':
      return 'bottom-end';
    case 'bottom-end':
      return 'bottom-start';
    default:
      return popperPlacement;
  }
}
function getArrowPosition(popperPlacement) {
  var arrowPositionMappings = {
    auto: 'top',
    top: 'bottom',
    'top-start': 'bottom-left',
    'top-end': 'bottom-right',
    right: 'left',
    'right-start': 'left-top',
    'right-end': 'left-bottom',
    bottom: 'top',
    'bottom-start': 'top-left',
    'bottom-end': 'top-right',
    left: 'right',
    'left-start': 'right-top',
    'left-end': 'right-bottom'
  };
  return popperPlacement ? arrowPositionMappings[popperPlacement] : 'top';
}
function getMenuPosition(popperPlacement) {
  if (popperPlacement === 'auto') {
    return 'bottom';
  }
  return popperPlacement ? popperPlacement.split('-')[0] : 'bottom';
}

function _templateObject$3() {
  var data = _taggedTemplateLiteral(["\n  /* stylelint-disable-next-line declaration-no-important */\n  position: static !important; /* [1] */\n  min-width: 180px;\n  max-height: ", ";\n  overflow-y: auto;\n\n  ", ";\n\n  ", ";\n"]);
  _templateObject$3 = function _templateObject() {
    return data;
  };
  return data;
}
var COMPONENT_ID$1 = 'dropdowns.menu';
var StyledMenu = styled.ul.attrs(function (props) {
  return {
    'data-garden-id': COMPONENT_ID$1,
    'data-garden-version': '8.4.0',
    className: props.isAnimated && 'is-animated'
  };
})(_templateObject$3(), function (props) {
  return props.maxHeight;
}, function (props) {
  return props.hasArrow && arrowStyles(getArrowPosition(props.placement), {
    size: "".concat(props.theme.space.base * 2, "px"),
    inset: '2px',
    animationModifier: props.isAnimated ? '.is-animated' : undefined
  });
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$1, props);
});
StyledMenu.defaultProps = {
  theme: DEFAULT_THEME
};

function _templateObject$4() {
  var data = _taggedTemplateLiteral(["\n  ", ";\n\n  ", ";\n"]);
  _templateObject$4 = function _templateObject() {
    return data;
  };
  return data;
}
var COMPONENT_ID$2 = 'dropdowns.menu_wrapper';
var StyledMenuWrapper = styled.div.attrs(function (props) {
  return {
    'data-garden-id': COMPONENT_ID$2,
    'data-garden-version': '8.4.0',
    className: props.isAnimated && 'is-animated'
  };
})(_templateObject$4(), function (props) {
  return menuStyles(getMenuPosition(props.placement), {
    hidden: props.isHidden,
    margin: "".concat(props.theme.space.base * (props.hasArrow ? 2 : 1), "px"),
    zIndex: props.zIndex,
    animationModifier: props.isAnimated ? '.is-animated' : undefined
  });
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$2, props);
});
StyledMenuWrapper.defaultProps = {
  theme: DEFAULT_THEME
};

function _templateObject$5() {
  var data = _taggedTemplateLiteral(["\n  display: block;\n  margin: ", "px 0;\n  border-bottom: ", ";\n\n  ", ";\n"]);
  _templateObject$5 = function _templateObject() {
    return data;
  };
  return data;
}
var COMPONENT_ID$3 = 'dropdowns.separator';
var StyledSeparator = styled.li.attrs({
  'data-garden-id': COMPONENT_ID$3,
  'data-garden-version': '8.4.0',
  role: 'separator'
})(_templateObject$5(), function (props) {
  return props.theme.space.base;
}, function (props) {
  return "".concat(props.theme.borders.sm, " ").concat(getColor('neutralHue', 200, props.theme));
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$3, props);
});
StyledSeparator.defaultProps = {
  theme: DEFAULT_THEME
};

function _templateObject$6() {
  var data = _taggedTemplateLiteral(["\n  display: block;\n  position: relative; /* [1] */\n  z-index: 0; /* [2] */\n  cursor: ", ";\n  padding: ", ";\n  text-decoration: none;\n  line-height: ", "px;\n  word-wrap: break-word;\n  user-select: none;\n\n  &:first-child {\n    margin-top: ", "px;\n  }\n\n  &:last-child {\n    margin-bottom: ", "px;\n  }\n\n  &:focus {\n    outline: none;\n  }\n\n  ", "\n\n  ", ";\n"]);
  _templateObject$6 = function _templateObject() {
    return data;
  };
  return data;
}
var COMPONENT_ID$4 = 'dropdowns.item';
var getItemPaddingVertical = function getItemPaddingVertical(props) {
  if (props.isCompact) {
    return "".concat(props.theme.space.base * 1.5, "px");
  }
  return "".concat(props.theme.space.base * 2.5, "px");
};
var getItemPaddingHorizontal = function getItemPaddingHorizontal(props) {
  if (props.isCompact) {
    return "".concat(props.theme.space.base * 6, "px");
  }
  return "".concat(props.theme.space.base * 8, "px");
};
var getColorStyles = function getColorStyles(props) {
  return css(["background-color:", ";color:", ";"], props.isFocused && !props.disabled && getColor('primaryHue', 600, props.theme, 0.08), props.disabled ? getColor('neutralHue', 400, props.theme) : props.theme.colors.foreground);
};
var StyledItem = styled.li.attrs(function (props) {
  return {
    'data-garden-id': COMPONENT_ID$4,
    'data-garden-version': '8.4.0',
    'aria-disabled': props.disabled
  };
})(_templateObject$6(), function (props) {
  return props.disabled ? 'default' : 'pointer';
}, function (props) {
  return "".concat(getItemPaddingVertical(props), " ").concat(getItemPaddingHorizontal(props));
}, function (props) {
  return props.theme.space.base * 5;
}, function (props) {
  return props.theme.space.base * 2;
}, function (props) {
  return props.theme.space.base * 2;
}, function (props) {
  return getColorStyles(props);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$4, props);
});
StyledItem.defaultProps = {
  theme: DEFAULT_THEME
};

function _templateObject$7() {
  var data = _taggedTemplateLiteral(["\n  color: ", ";\n\n  ", ";\n"]);
  _templateObject$7 = function _templateObject() {
    return data;
  };
  return data;
}
var COMPONENT_ID$5 = 'dropdowns.add_item';
var StyledAddItem = styled(StyledItem).attrs({
  'data-garden-id': COMPONENT_ID$5,
  'data-garden-version': '8.4.0'
})(_templateObject$7(), function (props) {
  return !props.disabled && getColor('primaryHue', 600, props.theme);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$5, props);
});
StyledAddItem.defaultProps = {
  theme: DEFAULT_THEME
};

function _templateObject$8() {
  var data = _taggedTemplateLiteral(["\n  display: ", ";\n  line-height: ", "px;\n  color: ", ";\n  font-size: ", ";\n\n  ", ";\n"]);
  _templateObject$8 = function _templateObject() {
    return data;
  };
  return data;
}
var COMPONENT_ID$6 = 'dropdowns.item_meta';
var StyledItemMeta = styled.span.attrs({
  'data-garden-id': COMPONENT_ID$6,
  'data-garden-version': '8.4.0'
})(_templateObject$8(), function (props) {
  return props.isCompact ? 'none' : 'block';
}, function (props) {
  return props.theme.space.base * 4;
}, function (props) {
  return getColor('neutralHue', props.isDisabled ? 400 : 600, props.theme);
}, function (props) {
  return props.theme.fontSizes.sm;
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$6, props);
});
StyledItemMeta.defaultProps = {
  theme: DEFAULT_THEME
};

function _templateObject$9() {
  var data = _taggedTemplateLiteral(["\n  display: flex;\n  position: absolute;\n  top: 0;\n  ", ": 0;\n  align-items: center;\n  justify-content: center;\n  transition: opacity 0.1s ease-in-out;\n  opacity: ", ";\n  color: ", ";\n\n  ", ";\n\n  & > * {\n    ", ";\n  }\n"]);
  _templateObject$9 = function _templateObject() {
    return data;
  };
  return data;
}
var getSizeStyles = function getSizeStyles(props) {
  return css(["width:", ";height:calc(", "px + ", ");"], getItemPaddingHorizontal(props), props.theme.space.base * 5, math("".concat(getItemPaddingVertical(props), " * 2")));
};
var getIconSizeStyles = function getIconSizeStyles(props) {
  var size = props.theme.iconSizes.md;
  if (props.isCompact) {
    size = props.theme.iconSizes.sm;
  }
  return css(["width:", ";height:", ";"], size, size);
};
var StyledItemIcon = styled.div(_templateObject$9(), function (props) {
  return props.theme.rtl ? 'right' : 'left';
}, function (props) {
  return props.isVisible ? '1' : '0';
}, function (props) {
  return props.isDisabled ? 'inherit' : getColor('primaryHue', 600, props.theme);
}, function (props) {
  return getSizeStyles(props);
}, function (props) {
  return getIconSizeStyles(props);
});
StyledItemIcon.defaultProps = {
  theme: DEFAULT_THEME
};

function _templateObject$a() {
  var data = _taggedTemplateLiteral(["\n  ", " {\n    right: ", ";\n    left: ", ";\n  }\n\n  ", ";\n"]);
  _templateObject$a = function _templateObject() {
    return data;
  };
  return data;
}
var COMPONENT_ID$7 = 'dropdowns.next_item';
var StyledNextItem = styled(StyledItem).attrs({
  'data-garden-id': COMPONENT_ID$7,
  'data-garden-version': '8.4.0'
})(_templateObject$a(), StyledItemIcon, function (props) {
  return props.theme.rtl ? 'auto' : '0';
}, function (props) {
  return props.theme.rtl ? '0' : 'auto';
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$7, props);
});
StyledNextItem.defaultProps = {
  theme: DEFAULT_THEME
};

function _extends$3() { _extends$3 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3.apply(this, arguments); }

var _ref$2 =
/*#__PURE__*/
createElement("path", {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  d: "M4.5 15l6.6-6.6c.2-.2.2-.5 0-.7L4.5 1"
});

function SvgChevronRightStroke(props) {
  return createElement("svg", _extends$3({
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    focusable: "false",
    role: "presentation"
  }, props), _ref$2);
}

function _templateObject$b() {
  var data = _taggedTemplateLiteral(["\n  transform: ", ";\n  color: ", ";\n\n  ", ";\n"]);
  _templateObject$b = function _templateObject() {
    return data;
  };
  return data;
}
var COMPONENT_ID$8 = 'dropdowns.next_item_icon';
var NextIconComponent = function NextIconComponent(_ref) {
  var className = _ref.className;
  return React__default.createElement(SvgChevronRightStroke, {
    "data-garden-id": COMPONENT_ID$8,
    "data-garden-version": '8.4.0',
    className: className
  });
};
var StyledNextIcon = styled(NextIconComponent)(_templateObject$b(), function (props) {
  return props.theme.rtl && 'rotate(180deg)';
}, function (props) {
  return props.isDisabled ? 'inherit' : getColor('neutralHue', 600, props.theme);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$8, props);
});
StyledNextIcon.defaultProps = {
  theme: DEFAULT_THEME
};

function _templateObject$c() {
  var data = _taggedTemplateLiteral(["\n  font-weight: ", ";\n\n  ", ";\n"]);
  _templateObject$c = function _templateObject() {
    return data;
  };
  return data;
}
var COMPONENT_ID$9 = 'dropdowns.previous_item';
var StyledPreviousItem = styled(StyledItem).attrs({
  'data-garden-id': COMPONENT_ID$9,
  'data-garden-version': '8.4.0'
})(_templateObject$c(), function (props) {
  return props.theme.fontWeights.semibold;
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$9, props);
});
StyledPreviousItem.defaultProps = {
  theme: DEFAULT_THEME
};

function _extends$4() { _extends$4 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4.apply(this, arguments); }

var _ref$3 =
/*#__PURE__*/
createElement("path", {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  d: "M11.5 1L4.9 7.6c-.2.2-.2.5 0 .7l6.6 6.6"
});

function SvgChevronLeftStroke(props) {
  return createElement("svg", _extends$4({
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    focusable: "false",
    role: "presentation"
  }, props), _ref$3);
}

function _templateObject$d() {
  var data = _taggedTemplateLiteral(["\n  transform: ", ";\n  color: ", ";\n\n  ", ";\n"]);
  _templateObject$d = function _templateObject() {
    return data;
  };
  return data;
}
var COMPONENT_ID$a = 'dropdowns.previous_item_icon';
var PreviousIconComponent = function PreviousIconComponent(_ref) {
  var className = _ref.className;
  return React__default.createElement(SvgChevronLeftStroke, {
    "data-garden-id": COMPONENT_ID$a,
    "data-garden-version": '8.4.0',
    className: className
  });
};
var StyledPreviousIcon = styled(PreviousIconComponent)(_templateObject$d(), function (props) {
  return props.theme.rtl && 'rotate(180deg)';
}, function (props) {
  return props.isDisabled ? 'inherit' : getColor('neutralHue', 600, props.theme);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$a, props);
});
StyledPreviousIcon.defaultProps = {
  theme: DEFAULT_THEME
};

function _templateObject$e() {
  var data = _taggedTemplateLiteral(["\n  display: flex;\n  position: absolute;\n  top: 0;\n  bottom: 0;\n  align-items: center;\n  justify-content: center;\n  ", ": ", ";\n  color: ", ";\n\n  & > * {\n    width: ", ";\n    height: ", ";\n  }\n\n  ", ";\n"]);
  _templateObject$e = function _templateObject() {
    return data;
  };
  return data;
}
var COMPONENT_ID$b = 'dropdowns.header_icon';
var getIconSize$1 = function getIconSize(props) {
  if (props.isCompact) {
    return props.theme.iconSizes.sm;
  }
  return props.theme.iconSizes.md;
};
var StyledHeaderIcon = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$b,
  'data-garden-version': '8.4.0'
})(_templateObject$e(), function (props) {
  return props.theme.rtl ? 'right' : 'left';
}, function (props) {
  return "calc(".concat(math("".concat(getItemPaddingHorizontal(props), " - ").concat(getIconSize$1(props))), " / 2)");
}, function (props) {
  return getColor('neutralHue', 600, props.theme);
}, function (props) {
  return getIconSize$1(props);
}, function (props) {
  return getIconSize$1(props);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$b, props);
});
StyledHeaderIcon.defaultProps = {
  theme: DEFAULT_THEME
};

function _templateObject$f() {
  var data = _taggedTemplateLiteral(["\n  cursor: default;\n  padding-right: ", ";\n  padding-left: ", ";\n  font-weight: ", ";\n\n  ", ";\n"]);
  _templateObject$f = function _templateObject() {
    return data;
  };
  return data;
}
var COMPONENT_ID$c = 'dropdowns.header_item';
var getHorizontalPadding = function getHorizontalPadding(props) {
  if (props.hasIcon) {
    return undefined;
  }
  if (props.isCompact) {
    return "".concat(props.theme.space.base * 3, "px");
  }
  return "".concat(props.theme.space.base * 4, "px");
};
var StyledHeaderItem = styled(StyledItem).attrs({
  'data-garden-id': COMPONENT_ID$c,
  'data-garden-version': '8.4.0'
})(_templateObject$f(), function (props) {
  return getHorizontalPadding(props);
}, function (props) {
  return getHorizontalPadding(props);
}, function (props) {
  return props.theme.fontWeights.semibold;
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$c, props);
});
StyledHeaderItem.defaultProps = {
  theme: DEFAULT_THEME
};

function _templateObject$g() {
  var data = _taggedTemplateLiteral(["\n  float: ", ";\n  margin-top: ", ";\n  /* stylelint-disable-next-line property-no-unknown,declaration-no-important */\n  margin-", ": ", "px !important;\n  width: ", ";\n  height: ", ";\n\n  ", ";\n"]);
  _templateObject$g = function _templateObject() {
    return data;
  };
  return data;
}
var COMPONENT_ID$d = 'dropdowns.media_figure';
var getMediaFigureSize = function getMediaFigureSize(props) {
  if (props.isCompact) {
    return "".concat(props.theme.space.base * 6, "px");
  }
  return "".concat(props.theme.space.base * 8, "px");
};
var getMediaFigureMarginTop = function getMediaFigureMarginTop(props) {
  if (props.isCompact) {
    return undefined;
  }
  return '1px';
};
var StyledMediaFigure = styled(
function (_ref) {
  var children = _ref.children,
      isCompact = _ref.isCompact,
      theme = _ref.theme,
      props = _objectWithoutProperties(_ref, ["children", "isCompact", "theme"]);
  return (
    React__default.cloneElement(Children.only(children), props)
  );
}).attrs({
  'data-garden-id': COMPONENT_ID$d,
  'data-garden-version': '8.4.0'
})(_templateObject$g(), function (props) {
  return props.theme.rtl ? 'right' : 'left';
}, function (props) {
  return getMediaFigureMarginTop(props);
}, function (props) {
  return props.theme.rtl ? 'left' : 'right';
}, function (props) {
  return props.isCompact ? props.theme.space.base * 1.5 : props.theme.space.base * 2;
}, function (props) {
  return getMediaFigureSize(props);
}, function (props) {
  return getMediaFigureSize(props);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$d, props);
});
StyledMediaFigure.defaultProps = {
  theme: DEFAULT_THEME
};

function _templateObject$h() {
  var data = _taggedTemplateLiteral(["\n  display: block;\n  margin-top: ", ";\n  overflow: hidden;\n\n  ", ";\n"]);
  _templateObject$h = function _templateObject() {
    return data;
  };
  return data;
}
var COMPONENT_ID$e = 'dropdowns.media_body';
var StyledMediaBody = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$e,
  'data-garden-version': '8.4.0'
})(_templateObject$h(), function (props) {
  return props.isCompact && math("".concat(math("".concat(getMediaFigureSize(props), " - ").concat(props.theme.space.base * 5, "px")), " / 2"));
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$e, props);
});
StyledMediaBody.defaultProps = {
  theme: DEFAULT_THEME
};

function _templateObject$i() {
  var data = _taggedTemplateLiteral(["\n  ", " {\n    height: ", ";\n  }\n\n  ", ";\n"]);
  _templateObject$i = function _templateObject() {
    return data;
  };
  return data;
}
var COMPONENT_ID$f = 'dropdowns.media_item';
var StyledMediaItem = styled(StyledItem).attrs({
  'data-garden-id': COMPONENT_ID$f,
  'data-garden-version': '8.4.0'
})(_templateObject$i(), StyledItemIcon, function (props) {
  return !props.isCompact && math("".concat(getMediaFigureSize(props), " + ").concat(math("".concat(getItemPaddingVertical(props), " * 2")), " + ").concat(math("".concat(getMediaFigureMarginTop(props), " * 2"))));
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$f, props);
});
StyledMediaItem.defaultProps = {
  theme: DEFAULT_THEME
};

var Trigger = function Trigger(_ref) {
  var children = _ref.children,
      refKey = _ref.refKey,
      triggerProps = _objectWithoutProperties(_ref, ["children", "refKey"]);
  var _useDropdownContext = useDropdownContext(),
      _useDropdownContext$d = _useDropdownContext.downshift,
      getRootProps = _useDropdownContext$d.getRootProps,
      getToggleButtonProps = _useDropdownContext$d.getToggleButtonProps,
      getInputProps = _useDropdownContext$d.getInputProps,
      isOpen = _useDropdownContext$d.isOpen;
  var hiddenInputRef = useRef(null);
  var triggerRef = useRef(null);
  var previousIsOpenRef = useRef(undefined);
  useEffect(function () {
    if (isOpen && !previousIsOpenRef.current) {
      hiddenInputRef.current && hiddenInputRef.current.focus();
    }
    if (!isOpen && previousIsOpenRef.current) {
      triggerRef.current && triggerRef.current.focus();
    }
    previousIsOpenRef.current = isOpen;
  }, [isOpen]);
  var renderChildren = function renderChildren(popperRef) {
    var _getRootProps = getRootProps(),
        rootPropsRefCallback = _getRootProps.ref,
        rootProps = _objectWithoutProperties(_getRootProps, ["ref"]);
    return React__default.cloneElement(React__default.Children.only(children), _objectSpread2({}, getToggleButtonProps(_objectSpread2({}, rootProps, {
      'aria-labelledby': undefined
    }, triggerProps, {}, children.props)), _defineProperty({}, refKey, function (childRef) {
      popperRef(childRef);
      triggerRef.current = childRef;
      rootPropsRefCallback(childRef);
    })));
  };
  return React__default.createElement(Reference, null, function (_ref2) {
    var popperReference = _ref2.ref;
    return React__default.createElement(React__default.Fragment, null, renderChildren(popperReference), React__default.createElement(StyledInput, getInputProps({
      readOnly: true,
      isHidden: true,
      tabIndex: -1,
      ref: hiddenInputRef,
      value: ''
    })));
  });
};
Trigger.propTypes = {
  children: PropTypes.any,
  refKey: PropTypes.string
};
Trigger.defaultProps = {
  refKey: 'ref'
};

var FieldContext = React__default.createContext(undefined);
var useFieldContext = function useFieldContext() {
  var fieldContext = useContext(FieldContext);
  if (!fieldContext) {
    throw new Error('This component must be rendered within a `Field` component.');
  }
  return fieldContext;
};

var Autocomplete = React__default.forwardRef(function (_ref, ref) {
  var children = _ref.children,
      controlledInputRef = _ref.inputRef,
      props = _objectWithoutProperties(_ref, ["children", "inputRef"]);
  var _useDropdownContext = useDropdownContext(),
      popperReferenceElementRef = _useDropdownContext.popperReferenceElementRef,
      _useDropdownContext$d = _useDropdownContext.downshift,
      getToggleButtonProps = _useDropdownContext$d.getToggleButtonProps,
      getInputProps = _useDropdownContext$d.getInputProps,
      isOpen = _useDropdownContext$d.isOpen;
  var _useFieldContext = useFieldContext(),
      isLabelHovered = _useFieldContext.isLabelHovered;
  var inputRef = useCombinedRefs(controlledInputRef);
  var triggerRef = useCombinedRefs(ref);
  var previousIsOpenRef = useRef(isOpen);
  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isFocused = _useState2[0],
      setIsFocused = _useState2[1];
  useEffect(function () {
    if (isOpen !== previousIsOpenRef.current) {
      inputRef.current && inputRef.current.focus();
    }
    previousIsOpenRef.current = isOpen;
  }, [inputRef, isOpen]);
  var selectProps = getToggleButtonProps(_objectSpread2({
    onKeyDown: function onKeyDown(e) {
      if (isOpen) {
        e.nativeEvent.preventDownshiftDefault = true;
      }
    }
  }, props));
  return React__default.createElement(Reference, null, function (_ref2) {
    var popperReference = _ref2.ref;
    return React__default.createElement(SelectWrapper, _extends({
      isHovered: isLabelHovered && !isOpen,
      isFocused: isOpen ? true : isFocused,
      isOpen: isOpen,
      tabIndex: null,
      ref: function ref(selectRef) {
        popperReference(selectRef);
        triggerRef.current = selectRef;
        popperReferenceElementRef.current = selectRef;
      }
    }, selectProps), !isOpen && React__default.createElement(StyledOverflowWrapper, null, children), React__default.createElement(StyledInput, getInputProps({
      isHidden: !isOpen,
      disabled: props.disabled,
      onFocus: function onFocus() {
        setIsFocused(true);
      },
      onBlur: function onBlur() {
        setIsFocused(false);
      },
      ref: inputRef
    })));
  });
});
Autocomplete.displayName = 'Autocomplete';
Autocomplete.propTypes = {
  isCompact: PropTypes.bool,
  isBare: PropTypes.bool,
  disabled: PropTypes.bool,
  focusInset: PropTypes.bool,
  isOpen: PropTypes.bool,
  validation: PropTypes.oneOf(['success', 'warning', 'error'])
};

var Multiselect = React__default.forwardRef(function (_ref, ref) {
  var renderItem = _ref.renderItem,
      placeholder = _ref.placeholder,
      maxItems = _ref.maxItems,
      renderShowMore = _ref.renderShowMore,
      externalInputRef = _ref.inputRef,
      props = _objectWithoutProperties(_ref, ["renderItem", "placeholder", "maxItems", "renderShowMore", "inputRef"]);
  var _useDropdownContext = useDropdownContext(),
      popperReferenceElementRef = _useDropdownContext.popperReferenceElementRef,
      _useDropdownContext$s = _useDropdownContext.selectedItems,
      selectedItems = _useDropdownContext$s === void 0 ? [] : _useDropdownContext$s,
      containsMultiselectRef = _useDropdownContext.containsMultiselectRef,
      _useDropdownContext$d = _useDropdownContext.downshift,
      getToggleButtonProps = _useDropdownContext$d.getToggleButtonProps,
      getInputProps = _useDropdownContext$d.getInputProps,
      isOpen = _useDropdownContext$d.isOpen,
      closeMenu = _useDropdownContext$d.closeMenu,
      inputValue = _useDropdownContext$d.inputValue,
      setDownshiftState = _useDropdownContext$d.setState,
      itemToString = _useDropdownContext$d.itemToString;
  var _useFieldContext = useFieldContext(),
      isLabelHovered = _useFieldContext.isLabelHovered;
  var inputRef = useCombinedRefs(externalInputRef);
  var triggerRef = useCombinedRefs(popperReferenceElementRef, ref);
  var blurTimeoutRef = useRef();
  var previousIsOpenRef = useRef(undefined);
  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isFocused = _useState2[0],
      setIsFocused = _useState2[1];
  var _useState3 = useState(undefined),
      _useState4 = _slicedToArray(_useState3, 2),
      focusedItem = _useState4[0],
      setFocusedItem = _useState4[1];
  var _useSelection = useSelection({
    rtl: isRtl(props),
    focusedItem: focusedItem,
    selectedItem: undefined,
    onFocus: function onFocus(item) {
      setFocusedItem(item);
    }
  }),
      getContainerProps = _useSelection.getContainerProps,
      getItemProps = _useSelection.getItemProps;
  useEffect(function () {
    containsMultiselectRef.current = true;
    var tempRef = blurTimeoutRef;
    return function () {
      clearTimeout(tempRef.current);
    };
  }, []);
  useEffect(function () {
    if (isOpen && !previousIsOpenRef.current) {
      inputRef.current && inputRef.current.focus();
    }
    previousIsOpenRef.current = isOpen;
  }, [isOpen, inputRef]);
  useEffect(function () {
    if (focusedItem !== undefined && isOpen) {
      closeMenu();
    }
  }, [focusedItem, isOpen, closeMenu]);
  var selectProps = getToggleButtonProps(_objectSpread2({
    tabIndex: props.disabled ? undefined : -1,
    onKeyDown: function onKeyDown(e) {
      if (isOpen) {
        e.nativeEvent.preventDownshiftDefault = true;
      } else if (!inputValue && e.keyCode === KEY_CODES.HOME) {
        setFocusedItem(selectedItems[0]);
        e.preventDefault();
      }
    },
    onFocus: function onFocus() {
      setIsFocused(true);
    },
    onBlur: function onBlur(e) {
      var currentTarget = e.currentTarget;
      blurTimeoutRef.current = setTimeout(function () {
        if (!currentTarget.contains(document.activeElement)) {
          setIsFocused(false);
        }
      }, 0);
    }
  }, props));
  var renderSelectableItem = useCallback(function (item, index) {
    var removeValue = function removeValue() {
      setDownshiftState({
        type: REMOVE_ITEM_STATE_TYPE,
        selectedItem: item
      });
      inputRef.current && inputRef.current.focus();
    };
    var renderedItem = renderItem({
      value: item,
      removeValue: removeValue
    });
    var focusRef = React__default.createRef();
    var clonedChild = React__default.cloneElement(renderedItem, getItemProps({
      item: item,
      focusRef: focusRef,
      onKeyDown: function onKeyDown(e) {
        if (e.keyCode === KEY_CODES.DELETE || e.keyCode === KEY_CODES.BACKSPACE) {
          e.preventDefault();
          removeValue();
        }
        if (e.keyCode === KEY_CODES.END && !inputValue) {
          inputRef.current && inputRef.current.focus();
          e.preventDefault();
        }
        if (isRtl(props)) {
          if (e.keyCode === KEY_CODES.RIGHT && index === 0) {
            e.preventDefault();
          }
          if (e.keyCode === KEY_CODES.LEFT && index === selectedItems.length - 1) {
            e.preventDefault();
            inputRef.current && inputRef.current.focus();
          }
        } else {
          if (e.keyCode === KEY_CODES.LEFT && index === 0) {
            e.preventDefault();
          }
          if (e.keyCode === KEY_CODES.RIGHT && index === selectedItems.length - 1) {
            e.preventDefault();
            inputRef.current && inputRef.current.focus();
          }
        }
      },
      onClick: function onClick(e) {
        e.nativeEvent.preventDownshiftDefault = true;
      },
      tabIndex: -1
    }));
    var key = "".concat(itemToString(item), "-").concat(index);
    return React__default.createElement(StyledItemWrapper, {
      key: key
    }, clonedChild);
  }, [getItemProps, inputValue, renderItem, setDownshiftState, itemToString, selectedItems, props, inputRef]);
  var items = useMemo(function () {
    var itemValues = selectedItems || [];
    var output = [];
    for (var x = 0; x < itemValues.length; x++) {
      var item = itemValues[x];
      if (x < maxItems) {
        if (props.disabled) {
          var renderedItem = renderItem({
            value: item,
            removeValue: function removeValue() {
              return undefined;
            }
          });
          output.push(React__default.createElement(StyledItemWrapper, {
            key: x
          }, renderedItem));
        } else {
          output.push(renderSelectableItem(item, x));
        }
      } else if (!isFocused && !inputValue || props.disabled) {
        output.push(React__default.createElement(StyledItemWrapper, {
          key: "more-anchor"
        }, React__default.createElement(StyledMoreAnchor, {
          isCompact: props.isCompact,
          isDisabled: props.disabled,
          onMouseDown: function onMouseDown(e) {
            e.preventDefault();
          }
        }, renderShowMore ? renderShowMore(itemValues.length - x) : "+ ".concat(itemValues.length - x, " more"))));
        break;
      } else {
        output.push(renderSelectableItem(item, x));
      }
    }
    return output;
  }, [isFocused, props.disabled, renderSelectableItem, selectedItems, renderItem, inputValue, maxItems, renderShowMore, props.isCompact]);
  return React__default.createElement(Reference, null, function (_ref2) {
    var popperReference = _ref2.ref;
    return React__default.createElement(StyledMultiSelect, getContainerProps(_objectSpread2({}, selectProps, {
      tagLayout: true,
      isHovered: isLabelHovered && !isOpen,
      isFocused: isOpen ? true : isFocused,
      isOpen: isOpen,
      ref: function ref(selectRef) {
        popperReference(selectRef);
        triggerRef.current = selectRef;
      }
    })), items, React__default.createElement(StyledMultiselectInput, getInputProps({
      disabled: props.disabled,
      onFocus: function onFocus() {
        setFocusedItem(undefined);
      },
      onClick: function onClick(e) {
        if (inputValue && inputValue.length > 0 && isOpen) {
          e.nativeEvent.preventDownshiftDefault = true;
        }
      },
      onKeyDown: function onKeyDown(e) {
        if (!inputValue) {
          if (isRtl(props) && e.keyCode === KEY_CODES.RIGHT && selectedItems.length > 0) {
            setFocusedItem(selectedItems[selectedItems.length - 1]);
          } else if (!isRtl(props) && e.keyCode === KEY_CODES.LEFT && selectedItems.length > 0) {
            setFocusedItem(selectedItems[selectedItems.length - 1]);
          } else if (e.keyCode === KEY_CODES.BACKSPACE && selectedItems.length > 0) {
            setDownshiftState({
              type: REMOVE_ITEM_STATE_TYPE,
              selectedItem: selectedItems[selectedItems.length - 1]
            });
            e.nativeEvent.preventDownshiftDefault = true;
            e.preventDefault();
            e.stopPropagation();
          }
        }
      },
      isVisible: isFocused || inputValue || selectedItems.length === 0,
      isCompact: props.isCompact,
      ref: inputRef,
      placeholder: selectedItems.length === 0 ? placeholder : undefined
    })));
  });
});
Multiselect.propTypes = {
  isCompact: PropTypes.bool,
  isBare: PropTypes.bool,
  disabled: PropTypes.bool,
  focusInset: PropTypes.bool,
  isOpen: PropTypes.bool,
  renderItem: PropTypes.func.isRequired,
  maxItems: PropTypes.number,
  validation: PropTypes.oneOf(['success', 'warning', 'error'])
};
Multiselect.defaultProps = {
  maxItems: 4
};
var Multiselect$1 = withTheme(Multiselect);

var Select = React__default.forwardRef(function (_ref, ref) {
  var children = _ref.children,
      props = _objectWithoutProperties(_ref, ["children"]);
  var _useDropdownContext = useDropdownContext(),
      popperReferenceElementRef = _useDropdownContext.popperReferenceElementRef,
      _useDropdownContext$d = _useDropdownContext.downshift,
      getToggleButtonProps = _useDropdownContext$d.getToggleButtonProps,
      getInputProps = _useDropdownContext$d.getInputProps,
      isOpen = _useDropdownContext$d.isOpen;
  var _useFieldContext = useFieldContext(),
      isLabelHovered = _useFieldContext.isLabelHovered;
  var hiddenInputRef = useRef(null);
  var triggerRef = useCombinedRefs(ref, popperReferenceElementRef);
  var previousIsOpenRef = useRef(undefined);
  useEffect(function () {
    if (isOpen && !previousIsOpenRef.current) {
      hiddenInputRef.current && hiddenInputRef.current.focus();
    }
    if (!isOpen && previousIsOpenRef.current) {
      triggerRef.current && triggerRef.current.focus();
    }
    previousIsOpenRef.current = isOpen;
  }, [isOpen, triggerRef]);
  var selectProps = getToggleButtonProps(_objectSpread2({
    tabIndex: props.disabled ? undefined : 0
  }, props));
  return React__default.createElement(Reference, null, function (_ref2) {
    var popperReference = _ref2.ref;
    return React__default.createElement(SelectWrapper, _extends({
      isHovered: isLabelHovered && !isOpen,
      isFocused: isOpen,
      isOpen: isOpen
    }, selectProps, {
      ref: function ref(selectRef) {
        popperReference(selectRef);
        triggerRef.current = selectRef;
        popperReferenceElementRef.current = selectRef;
      }
    }), React__default.createElement(StyledOverflowWrapper, null, children), React__default.createElement(StyledInput, getInputProps({
      readOnly: true,
      isHidden: true,
      tabIndex: -1,
      ref: hiddenInputRef,
      value: ''
    })));
  });
});
Select.displayName = 'Select';
Select.propTypes = {
  tagLayout: PropTypes.bool,
  isCompact: PropTypes.bool,
  isBare: PropTypes.bool,
  disabled: PropTypes.bool,
  focusInset: PropTypes.bool,
  isOpen: PropTypes.bool,
  validation: PropTypes.oneOf(['success', 'warning', 'error'])
};

var Field = function Field(props) {
  var _useDropdownContext = useDropdownContext(),
      getRootProps = _useDropdownContext.downshift.getRootProps;
  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isLabelHovered = _useState2[0],
      setIsLabelHovered = _useState2[1];
  return React__default.createElement(FieldContext.Provider, {
    value: {
      isLabelHovered: isLabelHovered,
      setIsLabelHovered: setIsLabelHovered
    }
  }, React__default.createElement(Field$1, getRootProps(_objectSpread2({}, props, {
    refKey: 'ref'
  }))));
};

var Hint = React__default.forwardRef(function (props, ref) {
  return React__default.createElement(Hint$1, _extends({
    ref: ref
  }, props));
});
Hint.displayName = 'Hint';

var Label = React__default.forwardRef(function (_ref, ref) {
  var onMouseEnter = _ref.onMouseEnter,
      onMouseLeave = _ref.onMouseLeave,
      other = _objectWithoutProperties(_ref, ["onMouseEnter", "onMouseLeave"]);
  var _useDropdownContext = useDropdownContext(),
      getLabelProps = _useDropdownContext.downshift.getLabelProps;
  var _useFieldContext = useFieldContext(),
      setIsLabelHovered = _useFieldContext.setIsLabelHovered;
  var labelProps = getLabelProps(_objectSpread2({
    onMouseEnter: composeEventHandlers(onMouseEnter, function () {
      setIsLabelHovered(true);
    }),
    onMouseLeave: composeEventHandlers(onMouseLeave, function () {
      setIsLabelHovered(false);
    })
  }, other));
  return React__default.createElement(Label$1, _extends({
    ref: ref
  }, labelProps));
});
Label.displayName = 'Label';
Label.propTypes = {
  isRegular: PropTypes.bool
};

var Message = React__default.forwardRef(function (props, ref) {
  return React__default.createElement(Message$1, _extends({
    ref: ref
  }, props));
});
Message.displayName = 'Message';
Message.propTypes = {
  validation: PropTypes.oneOf(['success', 'warning', 'error'])
};

var MenuContext = React__default.createContext(undefined);
var useMenuContext = function useMenuContext() {
  var menuContext = useContext(MenuContext);
  if (!menuContext) {
    throw new Error('This component must be rendered within a `Menu` component.');
  }
  return menuContext;
};

var Menu = function Menu(props) {
  var placement = props.placement,
      popperModifiers = props.popperModifiers,
      eventsEnabled = props.eventsEnabled,
      isAnimated = props.isAnimated,
      maxHeight = props.maxHeight,
      menuStyle = props.style,
      zIndex = props.zIndex,
      isCompact = props.isCompact,
      children = props.children,
      otherProps = _objectWithoutProperties(props, ["placement", "popperModifiers", "eventsEnabled", "isAnimated", "maxHeight", "style", "zIndex", "isCompact", "children"]);
  var _useDropdownContext = useDropdownContext(),
      itemIndexRef = _useDropdownContext.itemIndexRef,
      previousIndexRef = _useDropdownContext.previousIndexRef,
      nextItemsHashRef = _useDropdownContext.nextItemsHashRef,
      popperReferenceElementRef = _useDropdownContext.popperReferenceElementRef,
      _useDropdownContext$d = _useDropdownContext.downshift,
      isOpen = _useDropdownContext$d.isOpen,
      getMenuProps = _useDropdownContext$d.getMenuProps;
  var scheduleUpdateRef = useRef(undefined);
  useEffect(function () {
    if (isOpen) {
      scheduleUpdateRef.current && scheduleUpdateRef.current();
    }
  });
  var _useState = useState(isOpen),
      _useState2 = _slicedToArray(_useState, 2),
      isVisible = _useState2[0],
      setVisible = _useState2[1];
  useEffect(function () {
    var timeout;
    if (isOpen) {
      setVisible(true);
    } else if (isAnimated) {
      timeout = setTimeout(function () {
        return setVisible(false);
      }, 200);
    } else {
      setVisible(false);
    }
    return function () {
      return clearTimeout(timeout);
    };
  }, [isOpen, isAnimated]);
  itemIndexRef.current = 0;
  nextItemsHashRef.current = {};
  previousIndexRef.current = undefined;
  var popperPlacement = isRtl(props) ? getRtlPopperPlacement(placement) : getPopperPlacement(placement);
  return React__default.createElement(MenuContext.Provider, {
    value: {
      itemIndexRef: itemIndexRef,
      isCompact: isCompact
    }
  }, React__default.createElement(Popper, {
    placement: popperPlacement,
    modifiers: popperModifiers
    ,
    eventsEnabled: isOpen && eventsEnabled
  }, function (_ref) {
    var ref = _ref.ref,
        style = _ref.style,
        scheduleUpdate = _ref.scheduleUpdate,
        currentPlacement = _ref.placement;
    var computedStyle = menuStyle;
    scheduleUpdateRef.current = scheduleUpdate;
    if (popperReferenceElementRef.current && popperReferenceElementRef.current.getBoundingClientRect) {
      computedStyle = _objectSpread2({
        width: popperReferenceElementRef.current.getBoundingClientRect().width
      }, menuStyle);
    }
    var menuProps = getMenuProps(_objectSpread2({
      placement: currentPlacement,
      isAnimated: isAnimated && (isOpen || isVisible)
    }, otherProps));
    return React__default.createElement(StyledMenuWrapper, {
      ref: isOpen ? ref : undefined,
      hasArrow: menuProps.hasArrow,
      placement: menuProps.placement,
      style: style,
      isHidden: !isOpen,
      isAnimated: menuProps.isAnimated,
      zIndex: zIndex
    }, React__default.createElement(StyledMenu, _extends({
      isCompact: isCompact,
      maxHeight: maxHeight,
      style: computedStyle
    }, menuProps), (isOpen || isVisible) && children));
  }));
};
Menu.propTypes = {
  popperModifiers: PropTypes.any,
  eventsEnabled: PropTypes.bool,
  zIndex: PropTypes.number,
  style: PropTypes.object,
  placement: PropTypes.oneOf(['auto', 'top', 'top-start', 'top-end', 'end', 'end-top', 'end-bottom', 'bottom', 'bottom-start', 'bottom-end', 'start', 'start-top', 'start-bottom']),
  isAnimated: PropTypes.bool,
  isCompact: PropTypes.bool,
  hasArrow: PropTypes.bool,
  maxHeight: PropTypes.string
};
Menu.defaultProps = {
  placement: 'bottom-start',
  isAnimated: true,
  eventsEnabled: true,
  maxHeight: '400px',
  zIndex: 1000
};
var Menu$1 = withTheme(Menu);

var Separator = React__default.forwardRef(function (props, ref) {
  return React__default.createElement(StyledSeparator, _extends({
    ref: ref
  }, props));
});
Separator.displayName = 'Separator';

function _extends$5() { _extends$5 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5.apply(this, arguments); }

var _ref$4 =
/*#__PURE__*/
createElement("path", {
  stroke: "currentColor",
  strokeLinecap: "round",
  d: "M7.5 2.5v12m6-6h-12"
});

function SvgPlusStroke(props) {
  return createElement("svg", _extends$5({
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    focusable: "false",
    role: "presentation"
  }, props), _ref$4);
}

function _extends$6() { _extends$6 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6.apply(this, arguments); }

var _ref$5 =
/*#__PURE__*/
createElement("path", {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  strokeLinejoin: "round",
  d: "M1 9l4 4L15 3"
});

function SvgCheckLgStroke(props) {
  return createElement("svg", _extends$6({
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    focusable: "false",
    role: "presentation"
  }, props), _ref$5);
}

var ItemContext = React__default.createContext(undefined);
var useItemContext = function useItemContext() {
  var context = useContext(ItemContext);
  if (!context) {
    throw new Error('This component must be rendered within an `Item` component.');
  }
  return context;
};

var Item = React__default.forwardRef(function (_ref, ref) {
  var value = _ref.value,
      disabled = _ref.disabled,
      _ref$component = _ref.component,
      component = _ref$component === void 0 ? StyledItem : _ref$component,
      children = _ref.children,
      props = _objectWithoutProperties(_ref, ["value", "disabled", "component", "children"]);
  var _useDropdownContext = useDropdownContext(),
      selectedItems = _useDropdownContext.selectedItems,
      _useDropdownContext$d = _useDropdownContext.downshift,
      isOpen = _useDropdownContext$d.isOpen,
      selectedItem = _useDropdownContext$d.selectedItem,
      highlightedIndex = _useDropdownContext$d.highlightedIndex,
      getItemProps = _useDropdownContext$d.getItemProps,
      setHighlightedIndex = _useDropdownContext$d.setHighlightedIndex,
      itemToString = _useDropdownContext$d.itemToString;
  var _useMenuContext = useMenuContext(),
      itemIndexRef = _useMenuContext.itemIndexRef,
      isCompact = _useMenuContext.isCompact;
  var Component = component;
  if ((value === undefined || value === null) && !disabled) {
    throw new Error('All Item components require a `value` prop');
  }
  var currentIndex = itemIndexRef.current;
  var isFocused = highlightedIndex === currentIndex;
  var isSelected;
  if (value) {
    if (selectedItems) {
      isSelected = selectedItems.some(function (item) {
        return itemToString(item) === itemToString(value);
      });
    } else {
      isSelected = itemToString(selectedItem) === itemToString(value);
    }
  } else {
    isSelected = false;
  }
  useEffect(function () {
    if (isOpen && !disabled && !selectedItems && isSelected) {
      setHighlightedIndex(currentIndex);
    }
  }, [currentIndex, disabled, isOpen, isSelected, selectedItems, setHighlightedIndex]);
  if (disabled) {
    return React__default.createElement(ItemContext.Provider, {
      value: {
        isDisabled: disabled
      }
    }, React__default.createElement(Component, _extends({
      ref: ref,
      disabled: disabled,
      isCompact: isCompact
    }, props), isSelected && React__default.createElement(StyledItemIcon, {
      isCompact: isCompact,
      isVisible: isSelected,
      isDisabled: disabled
    }, React__default.createElement(SvgCheckLgStroke, null)), children));
  }
  itemIndexRef.current++;
  return React__default.createElement(ItemContext.Provider, {
    value: {
      isDisabled: disabled
    }
  }, React__default.createElement(Component, getItemProps(_objectSpread2({
    item: value,
    isFocused: isFocused,
    ref: ref,
    isCompact: isCompact
  }, props)), isSelected && React__default.createElement(StyledItemIcon, {
    isCompact: isCompact,
    isVisible: isSelected
  }, React__default.createElement(SvgCheckLgStroke, null)), children));
});
Item.displayName = 'Item';
Item.propTypes = {
  value: PropTypes.any,
  disabled: PropTypes.bool
};

var AddItemComponent = React__default.forwardRef(function (_ref, ref) {
  var children = _ref.children,
      disabled = _ref.disabled,
      props = _objectWithoutProperties(_ref, ["children", "disabled"]);
  var _useMenuContext = useMenuContext(),
      isCompact = _useMenuContext.isCompact;
  return React__default.createElement(StyledAddItem, _extends({
    ref: ref,
    disabled: disabled
  }, props), React__default.createElement(StyledItemIcon, {
    isCompact: isCompact,
    isVisible: true,
    isDisabled: disabled
  }, React__default.createElement(SvgPlusStroke, null)), children);
});
AddItemComponent.displayName = 'AddItemComponent';
var AddItem = React__default.forwardRef(function (props, ref) {
  return React__default.createElement(Item, _extends({
    component: AddItemComponent,
    ref: ref
  }, props));
});
AddItem.displayName = 'AddItem';
AddItem.propTypes = {
  value: PropTypes.any,
  disabled: PropTypes.bool
};

var HeaderIcon = React__default.forwardRef(function (props, ref) {
  var _useMenuContext = useMenuContext(),
      isCompact = _useMenuContext.isCompact;
  return React__default.createElement(StyledHeaderIcon, _extends({
    ref: ref,
    isCompact: isCompact
  }, props));
});
HeaderIcon.displayName = 'HeaderIcon';

var HeaderItem = React__default.forwardRef(function (props, ref) {
  var _useMenuContext = useMenuContext(),
      isCompact = _useMenuContext.isCompact;
  return React__default.createElement(StyledHeaderItem, _extends({
    ref: ref,
    isCompact: isCompact
  }, props));
});
HeaderItem.displayName = 'HeaderItem';

var ItemMeta = React__default.forwardRef(function (props, ref) {
  var _useMenuContext = useMenuContext(),
      isCompact = _useMenuContext.isCompact;
  var _useItemContext = useItemContext(),
      isDisabled = _useItemContext.isDisabled;
  return React__default.createElement(StyledItemMeta, _extends({
    ref: ref,
    isCompact: isCompact,
    isDisabled: isDisabled
  }, props));
});
ItemMeta.displayName = 'ItemMeta';

var MediaBody = React__default.forwardRef(function (props, ref) {
  var _useMenuContext = useMenuContext(),
      isCompact = _useMenuContext.isCompact;
  return React__default.createElement(StyledMediaBody, _extends({
    ref: ref,
    isCompact: isCompact
  }, props));
});
MediaBody.displayName = 'MediaBody';

var MediaFigure = function MediaFigure(props) {
  var _useMenuContext = useMenuContext(),
      isCompact = _useMenuContext.isCompact;
  return React__default.createElement(StyledMediaFigure, _extends({
    isCompact: isCompact
  }, props));
};

var MediaItem = React__default.forwardRef(function (props, ref) {
  return React__default.createElement(Item, _extends({
    component: StyledMediaItem,
    ref: ref
  }, props));
});
MediaItem.displayName = 'MediaItem';
MediaItem.propTypes = {
  value: PropTypes.any,
  disabled: PropTypes.bool
};

var NextItemComponent = React__default.forwardRef(function (_ref, ref) {
  var children = _ref.children,
      disabled = _ref.disabled,
      props = _objectWithoutProperties(_ref, ["children", "disabled"]);
  var _useMenuContext = useMenuContext(),
      isCompact = _useMenuContext.isCompact;
  return React__default.createElement(StyledNextItem, _extends({
    ref: ref,
    disabled: disabled
  }, props), React__default.createElement(StyledItemIcon, {
    isCompact: isCompact,
    isDisabled: disabled,
    isVisible: true
  }, React__default.createElement(StyledNextIcon, {
    isDisabled: disabled
  })), children);
});
NextItemComponent.displayName = 'NextItemComponent';
var NextItem = React__default.forwardRef(function (_ref2, ref) {
  var value = _ref2.value,
      disabled = _ref2.disabled,
      props = _objectWithoutProperties(_ref2, ["value", "disabled"]);
  var _useDropdownContext = useDropdownContext(),
      nextItemsHashRef = _useDropdownContext.nextItemsHashRef;
  var _useMenuContext2 = useMenuContext(),
      itemIndexRef = _useMenuContext2.itemIndexRef;
  if (!disabled) {
    nextItemsHashRef.current[value] = itemIndexRef.current;
  }
  return React__default.createElement(Item, _extends({
    component: NextItemComponent,
    "aria-expanded": true,
    disabled: disabled,
    value: value,
    ref: ref
  }, props));
});
NextItem.displayName = 'NextItem';
NextItem.propTypes = {
  value: PropTypes.any,
  disabled: PropTypes.bool
};

var PreviousItemComponent = React__default.forwardRef(function (_ref, ref) {
  var children = _ref.children,
      disabled = _ref.disabled,
      props = _objectWithoutProperties(_ref, ["children", "disabled"]);
  var _useMenuContext = useMenuContext(),
      isCompact = _useMenuContext.isCompact;
  return React__default.createElement(StyledPreviousItem, _extends({
    ref: ref,
    disabled: disabled
  }, props), React__default.createElement(StyledItemIcon, {
    isCompact: isCompact,
    isDisabled: disabled,
    isVisible: true
  }, React__default.createElement(StyledPreviousIcon, {
    isDisabled: disabled
  })), children);
});
PreviousItemComponent.displayName = 'PreviousItemComponent';
var PreviousItem = React__default.forwardRef(function (_ref2, ref) {
  var value = _ref2.value,
      disabled = _ref2.disabled,
      props = _objectWithoutProperties(_ref2, ["value", "disabled"]);
  var _useDropdownContext = useDropdownContext(),
      previousIndexRef = _useDropdownContext.previousIndexRef;
  var _useMenuContext2 = useMenuContext(),
      itemIndexRef = _useMenuContext2.itemIndexRef;
  if (!disabled) {
    previousIndexRef.current = itemIndexRef.current;
  }
  return React__default.createElement(Item, _extends({
    component: PreviousItemComponent,
    "aria-expanded": true,
    value: value,
    disabled: disabled
  }, props, {
    ref: ref
  }));
});
PreviousItem.displayName = 'PreviousItem';
PreviousItem.propTypes = {
  value: PropTypes.any,
  disabled: PropTypes.bool
};

export { AddItem, Autocomplete, Dropdown$1 as Dropdown, Field, HeaderIcon, HeaderItem, Hint, Item, ItemMeta, Label, MediaBody, MediaFigure, MediaItem, Menu$1 as Menu, Message, Multiselect$1 as Multiselect, NextItem, PreviousItem, Select, Separator, Trigger };
